import { Extension } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";

/**
 * An Opus Doc's first block is its title, saved as the resource's name — text
 * only. Anything else that lands in it (an image from the toolbar, a paste or
 * a drop, a formula) moves into a paragraph just below, rather than being
 * dropped on save. Line breaks are removed: the title is one line.
 */
export const OpusDocTitleGuard = Extension.create({
  name: "opusDocTitleGuard",
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: new PluginKey("opusDocTitleGuard"),
        appendTransaction: (_transactions, _oldState, state) => {
          // A read-only view must never write the document back.
          if (!editor.isEditable) return null;
          const title = state.doc.firstChild;
          if (!title?.isTextblock) return null;
          const ranges: { from: number; to: number }[] = [];
          const moved: ProseMirrorNode[] = [];
          title.forEach((child, offset) => {
            if (child.isText) return;
            // Inside the title, offset 0 is position 1.
            ranges.push({ from: 1 + offset, to: 1 + offset + child.nodeSize });
            if (child.type.name !== "hardBreak") moved.push(child);
          });
          if (ranges.length === 0) return null;
          const tr = state.tr;
          for (const range of ranges.reverse()) tr.delete(range.from, range.to);
          if (moved.length > 0) {
            const afterTitle = tr.doc.firstChild?.nodeSize ?? 0;
            tr.insert(
              afterTitle,
              state.schema.nodes.paragraph.create(null, moved),
            );
          }
          return tr;
        },
      }),
    ];
  },
});
